import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle, Download, Package, ShoppingBag, Loader2 } from "lucide-react";
import { API } from "../App";
import Footer from "../components/Footer";

export default function StoreSuccess({ auth }) {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    } else {
      setLoading(false);
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await fetch(`${API}/store/orders/${orderId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOrder(data);
      }
    } catch (error) {
      console.error("Failed to fetch order:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#A87C6D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full text-center animate-fade-in">
          {/* Success Icon */}
          <div className="w-20 h-20 rounded-full bg-[#7A9E7E]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#7A9E7E]" />
          </div>

          <h1 
            className="text-3xl font-light text-[#2D2A26] mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Thank you for your order
          </h1>

          <p className="text-[#6E6A66] mb-8">
            {order 
              ? `Order #${order.order_id} has been confirmed.`
              : "Your order has been confirmed."
            }
          </p>

          {order && (
            <div className="bg-white rounded-2xl border border-[#E6E2DD] p-6 mb-8 text-left">
              {/* Order Items */}
              <div className="space-y-3 mb-6">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start">
                    <div>
                      <span className="text-[#2D2A26]">{item.name}</span>
                      <span className="text-[#A8A4A0] ml-2">x{item.quantity}</span>
                      {item.product_type === "digital" && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-[#A87C6D]/10 text-[#A87C6D] rounded-full">
                          Digital
                        </span>
                      )}
                    </div>
                    <span className="text-[#6E6A66]">
                      ${(item.unit_price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#E6E2DD] pt-4">
                <div className="flex justify-between font-medium text-[#2D2A26]">
                  <span>Total</span>
                  <span>${order.total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Digital Downloads */}
              {order.has_digital && (
                <div className="mt-6 p-4 bg-[#F5F2ED] rounded-xl">
                  <div className="flex items-center gap-2 text-[#A87C6D] mb-2">
                    <Download className="w-5 h-5" />
                    <span className="font-medium">Digital Downloads Ready</span>
                  </div>
                  <p className="text-sm text-[#6E6A66]">
                    Access your digital products anytime from your order history.
                  </p>
                  <Link to="/orders">
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 rounded-full border-[#A87C6D] text-[#A87C6D] hover:bg-[#A87C6D] hover:text-white"
                    >
                      View Downloads
                    </Button>
                  </Link>
                </div>
              )}

              {/* Shipping Info */}
              {order.has_physical && order.shipping_address && (
                <div className="mt-6 p-4 bg-[#F5F2ED] rounded-xl">
                  <div className="flex items-center gap-2 text-[#6E6A66] mb-2">
                    <Package className="w-5 h-5" />
                    <span className="font-medium">Shipping To</span>
                  </div>
                  <p className="text-sm text-[#6E6A66]">
                    {order.shipping_address.name}<br />
                    {order.shipping_address.street}<br />
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                  </p>
                  <p className="text-xs text-[#A8A4A0] mt-2">
                    You'll receive an email with tracking info when your order ships.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Guest order tracking info */}
          {order?.is_guest && (
            <div className="bg-[#F5F2ED] rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-[#6E6A66]">
                <strong>Save your order ID:</strong> {order.order_id}
              </p>
              <p className="text-sm text-[#A8A4A0] mt-1">
                You can track your order anytime at{" "}
                <Link to="/track-order" className="text-[#A87C6D] hover:underline">
                  Track Order
                </Link>
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {auth?.user ? (
              <Link to="/orders">
                <Button
                  variant="outline"
                  className="rounded-full px-6 border-[#E6E2DD] text-[#6E6A66] hover:bg-[#F5F2ED]"
                >
                  View Order History
                </Button>
              </Link>
            ) : (
              <Link to="/track-order">
                <Button
                  variant="outline"
                  className="rounded-full px-6 border-[#E6E2DD] text-[#6E6A66] hover:bg-[#F5F2ED]"
                >
                  Track Your Order
                </Button>
              </Link>
            )}
            <Link to="/store">
              <Button className="rounded-full px-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                Continue Shopping
              </Button>
            </Link>
          </div>

          {auth?.user && (
            <Link to="/dashboard" className="block mt-8">
              <span className="text-[#A8A4A0] hover:text-[#6E6A66] text-sm">
                ← Back to your space
              </span>
            </Link>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
